import { toast } from "sonner";
import baseApi from "store/api/baseApi";

const attendanceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Weekend API
    getWeekends: builder.query({
      query: () => ({
        url: "qwikITWeekend/",
        method: "GET",
      }),
      providesTags: ["weekend"],
    }),
    createWeekend: builder.mutation({
      query: data => ({
        url: "qwikITWeekend/new/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["weekend"],
      transformResponse: response => {
        toast.success("Weekend setup updated successfully");
        return response;
      },
    }),

    // Holiday API
    getHolidays: builder.query({
      query: () => ({
        url: "qwikITHoliday/",
        method: "GET",
      }),
      providesTags: ["holiday"],
    }),
    createHoliday: builder.mutation({
      query: data => ({
        url: "qwikITHoliday/new/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["holiday"],
      transformResponse: response => {
        toast.success("Holiday added successfully");
        return response;
      },
    }),
    updateHoliday: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `qwikITHoliday/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["holiday"],
      transformResponse: response => {
        toast.success("Holiday updated successfully");
        return response;
      },
    }),
    deleteHoliday: builder.mutation({
      query: id => ({
        url: `qwikITHoliday/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["holiday"],
      transformResponse: response => {
        toast.success("Holiday deleted successfully");
        return response;
      },
    }),

    // Device API
    getFingerprintDevices: builder.query({
      query: () => ({
        url: "qwikITFingerprintDevice/",
        method: "GET",
      }),
      providesTags: ["device"],
    }),
    createFingerprintDevice: builder.mutation({
      query: data => ({
        url: "qwikITFingerprintDevice/new/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["device"],
      transformResponse: response => {
        toast.success("Fingerprint device added successfully");
        return response;
      },
    }),
    updateFingerprintDevice: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `qwikITFingerprintDevice/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["device"],
      transformResponse: response => {
        toast.success("Device updated successfully");
        return response;
      },
    }),

    syncDeviceByIp: builder.mutation({
      query: data => ({
        url: "qwikITFingerprintDevice/sync_ip/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["attendance", "device"],
      transformResponse: response => {
        toast.success(response.message || "Successfully synced ZKTeco machine");
        return response;
      },
    }),

    getDeviceRegisteredUsers: builder.query({
      query: (params) => ({
        url: "qwikITFingerprintDevice/registered_users/",
        method: "GET",
        params,
      }),
      providesTags: ["device"],
    }),

    // Raw Log API
    getRawAttendanceLogs: builder.query({
      query: () => ({
        url: "qwikITRawAttendanceLog/",
        method: "GET",
      }),
      providesTags: ["attendance"],
    }),
    pushAttendanceLog: builder.mutation({
      query: data => ({
        url: "qwikITRawAttendanceLog/push/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["attendance"],
      transformResponse: response => {
        toast.success(response.message || "Punch log received successfully");
        return response;
      },
    }),

    // Attendance API
    getAttendance: builder.query({
      query: (params) => ({
        url: "qwikITAttendance/",
        method: "GET",
        params,
      }),
      providesTags: ["attendance"],
    }),
    updateAttendance: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `qwikITAttendance/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["attendance"],
      transformResponse: response => {
        toast.success("Attendance record updated");
        return response;
      },
    }),
    processAttendance: builder.mutation({
      query: data => ({
        url: "qwikITAttendance/process/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["attendance"],
      transformResponse: response => {
        toast.success(response.message || "Attendance processed successfully");
        return response;
      },
    }),

    // Payroll API
    getPayroll: builder.query({
      query: (params) => ({
        url: "qwikITPayroll/",
        method: "GET",
        params,
      }),
      providesTags: ["payroll"],
    }),
    updatePayroll: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `qwikITPayroll/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["payroll"],
      transformResponse: response => {
        toast.success("Payroll record updated");
        return response;
      },
    }),
    generatePayroll: builder.mutation({
      query: data => ({
        url: "qwikITPayroll/generate/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payroll"],
      transformResponse: response => {
        toast.success(response.message || "Monthly payroll generated successfully");
        return response;
      },
    }),
    createPayroll: builder.mutation({
      query: data => ({
        url: "qwikITPayroll/new/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payroll"],
      transformResponse: response => {
        toast.success("Payroll record generated");
        return response;
      },
    }),

    // Employee Profile & Base Salary API
    getEmployees: builder.query({
      query: () => ({
        url: "qwikITEmployee/",
        method: "GET",
      }),
      providesTags: ["user", "payroll"],
    }),
    createEmployee: builder.mutation({
      query: data => ({
        url: "qwikITEmployee/new/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["user", "payroll"],
      transformResponse: response => {
        toast.success("Employee salary record created");
        return response;
      },
    }),
    updateEmployee: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `qwikITEmployee/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["user", "payroll"],
      transformResponse: response => {
        toast.success("Employee salary updated successfully");
        return response;
      },
    }),
  }),
});

export const {
  useGetWeekendsQuery,
  useCreateWeekendMutation,
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useGetFingerprintDevicesQuery,
  useCreateFingerprintDeviceMutation,
  useUpdateFingerprintDeviceMutation,
  useSyncDeviceByIpMutation,
  useGetDeviceRegisteredUsersQuery,
  useLazyGetDeviceRegisteredUsersQuery,
  useGetRawAttendanceLogsQuery,
  usePushAttendanceLogMutation,
  useGetAttendanceQuery,
  useUpdateAttendanceMutation,
  useProcessAttendanceMutation,
  useGetPayrollQuery,
  useUpdatePayrollMutation,
  useGeneratePayrollMutation,
  useCreatePayrollMutation,
  useGetEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
} = attendanceApi;

export default attendanceApi;
